import { useEffect, useState } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Users, FileText, TrendingUp, Euro, Edit, Trash2, Plus, Star, Crown, Save } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function AdminPage() {
  const [stats, setStats] = useState(null);
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [editedPackages, setEditedPackages] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingPricing, setSavingPricing] = useState(false);
  
  const [brandDialog, setBrandDialog] = useState({ open: false, brand: null });
  const [modelDialog, setModelDialog] = useState({ open: false, model: null });
  const [equipmentDialog, setEquipmentDialog] = useState({ open: false, equipment: null });

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      const [statsRes, brandsRes, modelsRes, equipmentRes, pricingRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/stats`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/brands`),
        fetch(`${BACKEND_URL}/api/models`),
        fetch(`${BACKEND_URL}/api/equipment`),
        fetch(`${BACKEND_URL}/api/admin/promotion-pricing`, { credentials: 'include' })
      ]);

      const statsData = await statsRes.json();
      const brandsData = await brandsRes.json();
      const modelsData = await modelsRes.json();
      const equipmentData = await equipmentRes.json();
      const pricingData = await pricingRes.json();

      setStats(statsData);
      setBrands(brandsData);
      setModels(modelsData);
      setEquipment(equipmentData);
      setPricing(pricingData);
      setEditedPackages(pricingData.packages || {});
      setLoading(false);
    } catch (error) {
      console.error('Failed to load admin data:', error);
      toast.error('Nepodarilo sa načítať dáta');
      setLoading(false);
    }
  };

  const handleSaveBrand = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = { name: formData.get('name'), logo: formData.get('logo') || null };

    try {
      const url = brandDialog.brand
        ? `${BACKEND_URL}/api/brands/${brandDialog.brand.brand_id}`
        : `${BACKEND_URL}/api/brands`;
      
      const res = await fetch(url, {
        method: brandDialog.brand ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data)
      });

      if (!res.ok) throw new Error('Failed to save brand');

      toast.success('Značka uložená');
      setBrandDialog({ open: false, brand: null });
      loadAllData();
    } catch (error) {
      console.error('Failed to save brand:', error);
      toast.error('Nepodarilo sa uložiť značku');
    }
  };

  const handleDeleteBrand = async (brandId) => {
    if (!window.confirm('Naozaj chcete zmazať túto značku?')) return;

    try {
      const res = await fetch(`${BACKEND_URL}/api/brands/${brandId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!res.ok) throw new Error('Failed to delete');

      toast.success('Značka zmazaná');
      loadAllData();
    } catch (error) {
      console.error('Failed to delete:', error);
      toast.error('Nepodarilo sa zmazať značku');
    }
  };

  const handleSaveModel = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = { 
      brand_id: formData.get('brand_id'),
      name: formData.get('name')
    };

    try {
      const url = modelDialog.model
        ? `${BACKEND_URL}/api/models/${modelDialog.model.model_id}`
        : `${BACKEND_URL}/api/models`;
      
      const res = await fetch(url, {
        method: modelDialog.model ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data)
      });

      if (!res.ok) throw new Error('Failed to save model');

      toast.success('Model uložený');
      setModelDialog({ open: false, model: null });
      loadAllData();
    } catch (error) {
      console.error('Failed to save model:', error);
      toast.error('Nepodarilo sa uložiť model');
    }
  };

  const handleDeleteModel = async (modelId) => {
    if (!window.confirm('Naozaj chcete zmazať tento model?')) return;

    try {
      const res = await fetch(`${BACKEND_URL}/api/models/${modelId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!res.ok) throw new Error('Failed to delete');

      toast.success('Model zmazaný');
      loadAllData();
    } catch (error) {
      console.error('Failed to delete:', error);
      toast.error('Nepodarilo sa zmazať model');
    }
  };

  const handleSaveEquipment = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = { name: formData.get('name') };

    try {
      const url = equipmentDialog.equipment
        ? `${BACKEND_URL}/api/equipment/${equipmentDialog.equipment.equipment_id}`
        : `${BACKEND_URL}/api/equipment`;
      
      const res = await fetch(url, {
        method: equipmentDialog.equipment ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data)
      });

      if (!res.ok) throw new Error('Failed to save equipment');

      toast.success('Výbava uložená');
      setEquipmentDialog({ open: false, equipment: null });
      loadAllData();
    } catch (error) {
      console.error('Failed to save equipment:', error);
      toast.error('Nepodarilo sa uložiť výbavy');
    }
  };

  const handleDeleteEquipment = async (equipmentId) => {
    if (!window.confirm('Naozaj chcete zmazať túto výbavy?')) return;

    try {
      const res = await fetch(`${BACKEND_URL}/api/equipment/${equipmentId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!res.ok) throw new Error('Failed to delete');

      toast.success('Výbava zmazaná');
      loadAllData();
    } catch (error) {
      console.error('Failed to delete:', error);
      toast.error('Nepodarilo sa zmazať výbavy');
    }
  };

  const handlePriceChange = (packageId, newPrice) => {
    setEditedPackages(prev => ({
      ...prev,
      [packageId]: {
        ...prev[packageId],
        price: parseFloat(newPrice) || 0
      }
    }));
  };

  const handleSavePricing = async () => {
    setSavingPricing(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/promotion-pricing`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ packages: editedPackages })
      });

      if (!res.ok) throw new Error('Failed to save pricing');

      toast.success('Ceny uložené');
      loadAllData();
    } catch (error) {
      console.error('Failed to save pricing:', error);
      toast.error('Nepodarilo sa uložiť ceny');
    } finally {
      setSavingPricing(false);
    }
  };

  const premiumPackages = Object.entries(editedPackages).filter(([key]) => key.startsWith('premium'));
  const dealPackages = Object.entries(editedPackages).filter(([key]) => key.startsWith('deal'));

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-1 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-bold text-primary mb-8" style={{fontFamily: 'Manrope, sans-serif'}} data-testid="admin-heading">
            Admin panel
          </h1>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <Card className="p-6" data-testid="stats-users">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Používatelia</p>
                  <p className="text-3xl font-bold text-primary" style={{fontFamily: 'Manrope, sans-serif'}}>{stats?.total_users || 0}</p>
                </div>
                <Users className="h-10 w-10 text-accent" />
              </div>
            </Card>
            <Card className="p-6" data-testid="stats-listings">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Inzeráty</p>
                  <p className="text-3xl font-bold text-primary" style={{fontFamily: 'Manrope, sans-serif'}}>{stats?.total_listings || 0}</p>
                </div>
                <FileText className="h-10 w-10 text-accent" />
              </div>
            </Card>
            <Card className="p-6" data-testid="stats-promoted">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Propagované</p>
                  <p className="text-3xl font-bold text-primary" style={{fontFamily: 'Manrope, sans-serif'}}>{stats?.promoted_listings || 0}</p>
                </div>
                <TrendingUp className="h-10 w-10 text-accent" />
              </div>
            </Card>
            <Card className="p-6" data-testid="stats-revenue">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Príjmy</p>
                  <p className="text-3xl font-bold text-primary" style={{fontFamily: 'Manrope, sans-serif'}}>€{(stats?.total_revenue || 0).toFixed(0)}</p>
                </div>
                <Euro className="h-10 w-10 text-accent" />
              </div>
            </Card>
          </div>

          <Card className="p-6">
            <Tabs defaultValue="brands" className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="brands" data-testid="tab-brands">Značky</TabsTrigger>
                <TabsTrigger value="models" data-testid="tab-models">Modely</TabsTrigger>
                <TabsTrigger value="equipment" data-testid="tab-equipment">Výbava</TabsTrigger>
                <TabsTrigger value="pricing" data-testid="tab-pricing">Cenník</TabsTrigger>
              </TabsList>

              <TabsContent value="brands" className="space-y-4">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-semibold text-primary" style={{fontFamily: 'Manrope, sans-serif'}}>Značky</h2>
                  <Button
                    data-testid="add-brand-button"
                    onClick={() => setBrandDialog({ open: true, brand: null })}
                    className="bg-accent text-white hover:bg-lime-600"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Pridať
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-96 overflow-y-auto">
                  {brands.map(brand => (
                    <Card key={brand.brand_id} className="p-4 flex justify-between items-center" data-testid={`brand-item-${brand.brand_id}`}>
                      <span className="font-medium">{brand.name}</span>
                      <div className="flex gap-2">
                        <Button
                          data-testid={`edit-brand-${brand.brand_id}`}
                          onClick={() => setBrandDialog({ open: true, brand })}
                          variant="outline"
                          size="sm"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          data-testid={`delete-brand-${brand.brand_id}`}
                          onClick={() => handleDeleteBrand(brand.brand_id)}
                          variant="outline"
                          size="sm"
                          className="text-red-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </Card>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="models" className="space-y-4">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-semibold text-primary" style={{fontFamily: 'Manrope, sans-serif'}}>Modely</h2>
                  <Button
                    data-testid="add-model-button"
                    onClick={() => setModelDialog({ open: true, model: null })}
                    className="bg-accent text-white hover:bg-lime-600"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Pridať
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-96 overflow-y-auto">
                  {models.map(model => {
                    const brand = brands.find(b => b.brand_id === model.brand_id);
                    return (
                      <Card key={model.model_id} className="p-4 flex justify-between items-center" data-testid={`model-item-${model.model_id}`}>
                        <div>
                          <span className="font-medium">{model.name}</span>
                          <p className="text-sm text-slate-500">{brand?.name}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            data-testid={`edit-model-${model.model_id}`}
                            onClick={() => setModelDialog({ open: true, model })}
                            variant="outline"
                            size="sm"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            data-testid={`delete-model-${model.model_id}`}
                            onClick={() => handleDeleteModel(model.model_id)}
                            variant="outline"
                            size="sm"
                            className="text-red-600"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </TabsContent>

              <TabsContent value="equipment" className="space-y-4">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-semibold text-primary" style={{fontFamily: 'Manrope, sans-serif'}}>Výbava</h2>
                  <Button
                    data-testid="add-equipment-button"
                    onClick={() => setEquipmentDialog({ open: true, equipment: null })}
                    className="bg-accent text-white hover:bg-lime-600"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Pridať
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-h-96 overflow-y-auto">
                  {equipment.map(eq => (
                    <Card key={eq.equipment_id} className="p-4 flex justify-between items-center" data-testid={`equipment-item-${eq.equipment_id}`}>
                      <span className="font-medium">{eq.name}</span>
                      <div className="flex gap-2">
                        <Button
                          data-testid={`edit-equipment-${eq.equipment_id}`}
                          onClick={() => setEquipmentDialog({ open: true, equipment: eq })}
                          variant="outline"
                          size="sm"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          data-testid={`delete-equipment-${eq.equipment_id}`}
                          onClick={() => handleDeleteEquipment(eq.equipment_id)}
                          variant="outline"
                          size="sm"
                          className="text-red-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </Card>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="pricing" className="space-y-6">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-semibold text-primary" style={{fontFamily: 'Manrope, sans-serif'}}>Cenník propagovania</h2>
                  <Button
                    data-testid="save-pricing-button"
                    onClick={handleSavePricing}
                    disabled={savingPricing}
                    className="bg-green-600 text-white hover:bg-green-700"
                  >
                    <Save className="h-4 w-4 mr-1" />
                    {savingPricing ? 'Ukladám...' : 'Uložiť zmeny'}
                  </Button>
                </div>

                {/* Premium packages */}
                <Card className="p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Star className="h-5 w-5 text-amber-500" />
                    <h3 className="text-lg font-semibold">Premium inzeráty</h3>
                  </div>
                  <p className="text-sm text-slate-500 mb-4">Zobrazenie v sekcii Premium na hlavnej stránke (max 10 inzerátov)</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {premiumPackages.map(([key, pkg]) => (
                      <div key={key} className="p-4 border rounded-lg bg-amber-50">
                        <Label className="text-sm font-medium">{pkg.label}</Label>
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-slate-500">€</span>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={pkg.price}
                            onChange={(e) => handlePriceChange(key, e.target.value)}
                            className="w-24"
                            data-testid={`price-input-${key}`}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>

                {/* Deal of day packages */}
                <Card className="p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Crown className="h-5 w-5 text-green-600" />
                    <h3 className="text-lg font-semibold">Ponuka dňa</h3>
                  </div>
                  <p className="text-sm text-slate-500 mb-4">Exkluzívna pozícia na vrchu hlavnej stránky (len 1 inzerát)</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {dealPackages.map(([key, pkg]) => (
                      <div key={key} className="p-4 border rounded-lg bg-green-50">
                        <Label className="text-sm font-medium">{pkg.label}</Label>
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-slate-500">€</span>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={pkg.price}
                            onChange={(e) => handlePriceChange(key, e.target.value)}
                            className="w-24"
                            data-testid={`price-input-${key}`}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              </TabsContent>
            </Tabs>
          </Card>
        </div>
      </main>

      <Dialog open={brandDialog.open} onOpenChange={(open) => setBrandDialog({ open, brand: brandDialog.brand })}>
        <DialogContent data-testid="brand-dialog">
          <DialogHeader>
            <DialogTitle>{brandDialog.brand ? 'Upraviť značku' : 'Pridať značku'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveBrand} className="space-y-4">
            <div>
              <Label>Názov *</Label>
              <Input name="name" defaultValue={brandDialog.brand?.name} required data-testid="brand-name-input" />
            </div>
            <div>
              <Label>Logo URL</Label>
              <Input name="logo" defaultValue={brandDialog.brand?.logo || ''} data-testid="brand-logo-input" />
            </div>
            <Button type="submit" className="w-full" data-testid="save-brand-button">Uložiť</Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={modelDialog.open} onOpenChange={(open) => setModelDialog({ open, model: modelDialog.model })}>
        <DialogContent data-testid="model-dialog">
          <DialogHeader>
            <DialogTitle>{modelDialog.model ? 'Upraviť model' : 'Pridať model'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveModel} className="space-y-4">
            <div>
              <Label>Značka *</Label>
              <select name="brand_id" defaultValue={modelDialog.model?.brand_id} required className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2" data-testid="model-brand-select">
                <option value="">Vyberte značku</option>
                {brands.map(brand => (
                  <option key={brand.brand_id} value={brand.brand_id}>{brand.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Názov *</Label>
              <Input name="name" defaultValue={modelDialog.model?.name} required data-testid="model-name-input" />
            </div>
            <Button type="submit" className="w-full" data-testid="save-model-button">Uložiť</Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={equipmentDialog.open} onOpenChange={(open) => setEquipmentDialog({ open, equipment: equipmentDialog.equipment })}>
        <DialogContent data-testid="equipment-dialog">
          <DialogHeader>
            <DialogTitle>{equipmentDialog.equipment ? 'Upraviť výbavu' : 'Pridať výbavu'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveEquipment} className="space-y-4">
            <div>
              <Label>Názov *</Label>
              <Input name="name" defaultValue={equipmentDialog.equipment?.name} required data-testid="equipment-name-input" />
            </div>
            <Button type="submit" className="w-full" data-testid="save-equipment-button">Uložiť</Button>
          </form>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}

export default AdminPage;
